import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

export function useCancelReason() {
  const router = useRouter()
  const scheduleCallLinkNavigation = 'https://armoire.as.me/style-my-case'
  const scheduleCallLinkOperations = 'https://armoire.as.me/Feedback'
  // Initialize content templates
  const contentTemplates = ref(new Map([
    ['stylist-help', {
      category: 'styling',
      textPrimary: 'Did you know you can use the chat box in the lower right hand corner to get styling advice from our in-house stylists?<br> They can also add personalized rental recommendations directly to your closet. Chat with someone live M-F 9am – 5pm PST — or we\'ll get back to you the next business day!',
      actionTextPrimary: 'Request a Stylist Built Collection',
      actionPrimary: () => window.Intercom('showNewMessage')
    }],
    ['sizing-help', {
      category: 'sizing',
      textPrimary: '<b>Looking for a better fit?</b><br>Make sure your size preferences are up to date and include some range so we can recommend the best-fitting pieces.',
      actionTextPrimary: 'Update my Style Profile',
      actionPrimary: () => router.push({ name: 'style-profile' }),
      textSecondary: 'You can also use member reviews to find items that work for your body and style.',
      actionTextSecondary: 'Watch style sizing video',
      actionSecondary: () => window.open('https://youtu.be/2Z8uJVChnX4', '_blank'),
    }],
    ['customer-support', {
      category: 'quality',
      textPrimary: '<b>We care deeply about quality and accuracy, and we\'re here to make things right.</b><br> You can message us using the chat bubble in the bottom right corner of our site or email us. We\'ll get back to you as quickly as we can.',
      actionTextPrimary: 'Email Customer Support',
      actionPrimary: () => { window.location.href = 'mailto:hi@armoire.style?subject=Membership%20Inquiry' },
    }],
    ['member-experience', {
      category: 'navigation',
      textPrimary: '<b>Having trouble building your case?</b><br>Our Member Experience team would love to walk you through the easiest ways to build a case quickly and get the most out of your membership.',
      actionTextPrimary: 'Schedule a Call',
      actionPrimary: () => window.open(scheduleCallLinkNavigation, '_blank'),
      textSecondary: 'If you\'d rather explore on your own, our blog has helpful tips for navigating the site and building your case faster.',
      actionTextSecondary: 'How to find the good stuff',
      actionSecondary: () => window.open('https://blog.armoire.style/how-to-find-the-good-stuff-at-armoire/', '_blank'),
    }],
    ['operations-support', {
      category: 'operations',
      textPrimary: '<b>We\'re so sorry your experience missed the mark.</b><br>Our team is here to help. Schedule a quick call so we can make things right, answer any questions, and support your next steps.',
      actionTextPrimary: 'Schedule a Call',
      actionPrimary: () => window.open(scheduleCallLinkOperations, '_blank'),
    }],
    ['leadership-support', {
      category: 'operations',
      textPrimary: '<b>We\'re sorry we fell short.</b><br>We aim to provide exceptional support, and we\'re disappointed to hear we missed the mark. A member of our leadership team would love to connect and make things right.',
      actionTextPrimary: 'Schedule a Call',
      actionPrimary: () => window.open(scheduleCallLinkOperations, '_blank'),
    }],
    ['plan-options', {
      category: 'lifestyle',
      textPrimary: 'Maybe another plan will suit you better? We offer flexible plans to fit every budget.',
      actionTextPrimary: 'Change Plan',
      actionPrimary: () => router.push({name: 'change-plan'}),
      textSecondary: 'You can pause your membership without losing any of your member benefits or perks – like loyalty cash.',
      actionTextSecondary: 'Pause Membership',
      actionSecondary: () => router.push({name: 'pause'}),
    }]
  ]))

  // Map reasons to content templates using a content key
  const reasonMappings = ref(new Map([
    ['Not enough high priced/premium brands', 'stylist-help'],
    ['Clothes don\'t fit', 'sizing-help'],
    ['Styles I like not available in my size', 'sizing-help'],
    ['Not enough professional options', 'stylist-help'],
    ['Not enough unique / out of the ordinary items', 'stylist-help'],
    ['Clothes are too edgy or strange for my lifestyle', 'stylist-help'],
    ['I like the styles but I can\'t find anything new to rent', 'stylist-help'],
    
    // Clothing Quality reasons
    ['Clothes have a bad smell', 'customer-support'],
    ['Clothes are worn or damaged', 'customer-support'],
    ['Clothes look cheap', 'customer-support'],
    
    // Navigation/UX reasons
    ['Takes too long to build a case', 'member-experience'],
    ['Difficult to navigate the website', 'member-experience'],
    
    // Operations/Support reasons
    ['It is difficult to return my case', 'operations-support'],
    ['Missing or incorrect items in package', 'operations-support'],
    ['Shipping took too long', 'operations-support'],
    ['Poor customer support', 'leadership-support'],
    
    // Lifestyle reasons
    ['Only need rental items occasionally', 'plan-options'],
    ['Plan price is expensive', 'plan-options']
  ]))

  const getContentForReason = computed(() => {
    return (reason) => {
      const templateKey = reasonMappings.value.get(reason)
      return contentTemplates.value.get(templateKey) || null
    }
  })

  const buildContentMap = computed(() => {
    return (cancelReasons) => {
      const map = {}
      for (const reason of cancelReasons) {
        map[reason.id] = {}
        for (const secondaryReason of reason.secondaryReasons) {
          const content = getContentForReason.value(secondaryReason.reason)
          if (content) {
            map[reason.id][secondaryReason.id] = content
          }
        }
      }
      return map
    }
  })

  return {
    contentTemplates,
    reasonMappings,
    getContentForReason,
    buildContentMap,
  }
}